import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ProgressMeter,
  StatCard,
  StatCardRow,
} from '@/modules/common/components/molecules/stat-card';

import { getRatioColor } from '../../../helpers/progress-color.helper';
import { ApprovalsSummary } from '../../../types/time-tracking-approvals';
import { SummaryChip } from '../shared/summary-chip';
import { SummaryTooltipRows } from '../shared/summary-tooltip-rows';
import { useSummaryFormat } from '../shared/use-summary-format';

interface ApprovedForInvoiceCardProps {
  summary: ApprovalsSummary;
}

/**
 * Progress is "how much of what CAN be approved has been", so the approved hours are measured
 * against themselves plus the still-submitted ones. The bill card's denominator differs once
 * the two approved values diverge, which is why each card derives its own.
 */
const useApprovedForInvoice = (summary: ApprovalsSummary) => {
  const approved = summary.approvedForInvoiceHours;
  const submitted = summary.awaitingApprovalHours;
  const approvableTotal = approved + submitted;

  return {
    approved,
    submitted,
    approvableTotal,
    progressPct: approvableTotal > 0 ? (approved / approvableTotal) * 100 : null,
  };
};

const useApprovedForInvoiceTooltip = (summary: ApprovalsSummary) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount } = useSummaryFormat();
  const { approved, submitted } = useApprovedForInvoice(summary);

  return (
    <SummaryTooltipRows
      title={t('approvals.summaryCards.approvedForInvoice.title')}
      rows={[
        {
          label: t('approvals.summaryCards.approvedForInvoice.approvedLabel'),
          value: formatHoursAmount(approved),
        },
        {
          label: t('approvals.summaryCards.shared.awaitingLabel'),
          value: formatHoursAmount(submitted),
        },
        {
          label: t('approvals.summaryCards.shared.entriesPendingLabel'),
          value: String(summary.entriesPending),
        },
      ]}
      footer={t('approvals.summaryCards.approvedForInvoice.purposeNote')}
    />
  );
};

/** Hours that will be billed to the client. Same 2-rows-plus-meter shape as every other card. */
export const ApprovedForInvoiceCard = ({ summary }: ApprovedForInvoiceCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatHoursAmount, formatPct } = useSummaryFormat();
  const { approved, submitted, approvableTotal, progressPct } = useApprovedForInvoice(summary);

  return (
    <StatCard
      icon={<CircleCheck size={16} aria-hidden />}
      title={t('approvals.summaryCards.approvedForInvoice.title')}
      infoTooltip={t('approvals.summaryCards.approvedForInvoice.purposeNote')}
    >
      <StatCardRow
        label={t('approvals.summaryCards.shared.progressLabel')}
        value={
          progressPct === null ? (
            <span className="text-muted-foreground">
              {t('approvals.summaryCards.shared.noValue')}
            </span>
          ) : (
            <span
              className="text-base font-semibold tabular-nums"
              style={{ color: getRatioColor(progressPct) }}
            >
              {formatPct(progressPct)}
            </span>
          )
        }
      />
      <StatCardRow
        label={t('approvals.summaryCards.shared.entriesPendingLabel')}
        value={<span className="tabular-nums">{summary.entriesPending}</span>}
      />
      <ProgressMeter
        progress={approvableTotal > 0 ? approved / approvableTotal : null}
        filled={{
          label: t('approvals.summaryCards.approvedForInvoice.approvedShortLabel'),
          amount: approved,
        }}
        track={{ label: t('approvals.summaryCards.shared.awaitingLabel'), amount: submitted }}
        formatAmount={formatHoursAmount}
      />
    </StatCard>
  );
};

export const ApprovedForInvoiceChip = ({ summary }: ApprovedForInvoiceCardProps) => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { formatPct } = useSummaryFormat();
  const { progressPct } = useApprovedForInvoice(summary);
  const tooltip = useApprovedForInvoiceTooltip(summary);

  return (
    <SummaryChip
      icon={<CircleCheck className="h-3 w-3" />}
      label={t('approvals.summaryCards.approvedForInvoice.chipLabel')}
      value={
        progressPct === null ? t('approvals.summaryCards.shared.noValue') : formatPct(progressPct)
      }
      tooltip={tooltip}
    />
  );
};
